import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Navigation } from "./components/navigation";
import { Header } from "./components/header";
import About from "./components/about";
import { Features } from "./components/features";
import { Services } from "./components/services";
import { Icligm } from "./components/icligm";
import { Gallery } from "./components/gallery";
import { Testimonials } from "./components/testimonials";
import { Team } from "./components/Team";
import { Contact } from "./components/contact";
import { Iclchat } from "./components/iclchat";
import { Iclreact } from "./components/iclreact";
import { Privpol } from "./components/privpol";
import { Logindemo } from "./components/logindemo";
import { EmssQuots } from "./components/emss_quots";
import { IcligmQuots } from "./components/icligm_quots";
import JsonData from "./data/data.json";
//import SmoothScroll from "smooth-scroll";
import { SEOHelmet } from "./components/SEOHelmet";
import Modal from 'react-modal';
import "./App.css";

Modal.setAppElement('#root');

const PAGE_ROUTES = [
  { path: "/", anchor: "page-home" },
  { path: "/features/", anchor: "page-features" },
  { path: "/about/", anchor: "page-about" },
  { path: "/services/", anchor: "page-services" },
  { path: "/icligm/", anchor: "page-icligm" },
  { path: "/icl-chat/", anchor: "page-icl-chat" },
  { path: "/icl-react/", anchor: "page-icl-react" },
  { path: "/login-demo/", anchor: "page-login-demo" },
  { path: "/gallery/", anchor: "page-gallery" },
  { path: "/testimonials/", anchor: "page-testimonials" },
  { path: "/team/", anchor: "page-team" },
  { path: "/contact/", anchor: "page-contact" },
  { path: "/privacy-policy/", anchor: "page-privacy-policy" },
];

const normalisePathname = (pathname) => {
  if (pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
};

const RouteScrollSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastScrollDrivenPath = useRef(null);
  const isFirstRouteRender = useRef(true);
  const pendingProgrammaticPath = useRef(null);
  const pendingScrollTimer = useRef(null);

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useEffect(() => {
    const pathname = normalisePathname(location.pathname);
    const page = PAGE_ROUTES.find((item) => item.path === pathname);
    const hash = location.hash.replace("#", "");

    // An observer update already leaves the correct section in view.  Do not
    // start a second scroll when it replaces the address-bar URL.
    if (lastScrollDrivenPath.current === pathname) {
      lastScrollDrivenPath.current = null;
      return;
    }

    // A menu link changes the route before its destination is in view. Keep
    // the observer quiet until that smooth scroll has reached its section.
    pendingProgrammaticPath.current = pathname;
    window.clearTimeout(pendingScrollTimer.current);

    const targetId = hash || page?.anchor;
    const scrollBehavior = isFirstRouteRender.current ? "auto" : "smooth";
    const timeout = window.setTimeout(() => {
      if (pathname === "/" && !hash) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: scrollBehavior,
        });
      }

      const el = targetId && document.getElementById(targetId);
      if (el && pathname !== "/") {
        el.scrollIntoView({
          behavior: scrollBehavior,
          block: "start",
        });
      }

      pendingScrollTimer.current = window.setTimeout(() => {
        pendingProgrammaticPath.current = null;
      }, scrollBehavior === "auto" ? 0 : 900);
    }, 150);

    isFirstRouteRender.current = false;

    return () => {
      window.clearTimeout(timeout);
      window.clearTimeout(pendingScrollTimer.current);
    };
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const sections = PAGE_ROUTES.map(({ path, anchor }) => ({
      path,
      element: document.getElementById(anchor),
    })).filter(({ element }) => element);

    if (!sections.length || !("IntersectionObserver" in window)) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (pendingProgrammaticPath.current) return;

        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visible) return;

        const section = sections.find(({ element }) => element === visible.target);
        const currentPath = normalisePathname(window.location.pathname);
        if (!section || section.path === currentPath) return;

        lastScrollDrivenPath.current = section.path;
        navigate(section.path, { replace: true });
      },
      { rootMargin: "-42% 0px -48% 0px", threshold: [0, 0.01, 0.25] }
    );

    sections.forEach(({ element }) => observer.observe(element));
    return () => observer.disconnect();
  }, [navigate]);

  return null;
};

// Function to initialize Google Analytics
const initGoogleAnalytics = () => {
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag('js', new Date());
  gtag('config', 'G-HFVSCEEVZ0');
};

const App = () => {
  const [landingPageData, setLandingPageData] = useState({});
  const [featureData, setFeatureData] = useState(null);
  const [userData, setUserData] = useState(null);
  const emssRef = useRef(null);
  const icligmRef = useRef(null);
  const [signout, setSignout] = useState(false);
  const [quotAccessed, setQuotAccessed] = useState(false);
  const [iquotAccessed, setIquotAccessed] = useState(false);
  const firstLoad = useRef(true);

  useEffect(() => {
    initGoogleAnalytics();
    setLandingPageData(JsonData);
    firstLoad.current = false;
  }, []);

  const handleShowQuotation = (userID, compregkeyid, email, mobile, companyName) => {
    setUserData({ userID, compregkeyid, email, mobile, companyName });
    setQuotAccessed(true);
    setIquotAccessed(false);

    if (!firstLoad.current) {
      setTimeout(() => {
        emssRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }

  };

  const handleICLIGMShowQuotation = (userID, compregkeyid, email, mobile, companyName) => {
    setUserData({ userID, compregkeyid, email, mobile, companyName });
    setIquotAccessed(true);
    setQuotAccessed(false);
    if (!firstLoad.current) {
      setTimeout(() => {
        icligmRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }

      };

  useEffect(() => {
    initGoogleAnalytics();
    setLandingPageData(JsonData);
  }, []);

  const activePath = normalisePathname(window.location.pathname);
  const isStandalone = (path) => activePath === path;

  const scrollablePages = (
    <>
      <section id="page-home" data-route="/">
        <SEOHelmet>
          <title>
            ICL Software Solutions | Maritime Shipping | Importers-Exporters |
            International Trade | SaaS | Cloud | ChatBot Development |
            SCMTR | ICEGATE | Indian Customs
          </title>
          <meta
            name="description"
            content="Software solutions for Maritime Shipping, Importers-Exporters, International Trade, SaaS, Cloud, ChatBot Development, SCMTR, ICEGATE and Indian Customs."
          />
          <link rel="canonical" href="https://eximon.com/" />
        </SEOHelmet>
        <Header data={landingPageData.Header} />
      </section>
      <section id="page-features" data-route="/features/"><Features data={landingPageData.Features} apiData={featureData} isStandalone={isStandalone("/features/")} /></section>
      <section id="page-about" data-route="/about/"><About data={landingPageData.About} onFeatureDataLoaded={setFeatureData} isStandalone={isStandalone("/about/")} /></section>
      <section id="page-services" data-route="/services/"><Services data={landingPageData.Services} isStandalone={isStandalone("/services/")} /></section>
      <section id="page-icligm" data-route="/icligm/"><Icligm data={landingPageData.Icligm} isStandalone={isStandalone("/icligm/")} /></section>
      <section id="page-icl-chat" data-route="/icl-chat/"><Iclchat data={landingPageData.Iclchat} isStandalone={isStandalone("/icl-chat/")} /></section>
      <section id="page-icl-react" data-route="/icl-react/"><Iclreact data={landingPageData.Iclreact} isStandalone={isStandalone("/icl-react/")} /></section>
      <section id="page-login-demo" data-route="/login-demo/">
        <Logindemo
          data={landingPageData.Logindemo}
          onShowQuotation={handleShowQuotation}
          onShowICLIGMQuotation={handleICLIGMShowQuotation}
          signout={signout}
          setSignout={setSignout}
          setIquotAccessed={setIquotAccessed}
          setQuotAccessed={setQuotAccessed}
          isStandalone={isStandalone("/login-demo/")}
        />
        <div ref={emssRef}><EmssQuots data={landingPageData.EmssQuots} user={userData} setSignout={setSignout} setUserData={setUserData} quotAccessed={quotAccessed} /></div>
        <div ref={icligmRef}><IcligmQuots data={landingPageData.IcligmQuots} user={userData} setSignout={setSignout} setUserData={setUserData} iquotAccessed={iquotAccessed} onShowQuotation={handleICLIGMShowQuotation} /></div>
      </section>
      <section id="page-gallery" data-route="/gallery/"><Gallery data={landingPageData.Gallery} isStandalone={isStandalone("/gallery/")} /></section>
      <section id="page-testimonials" data-route="/testimonials/"><Testimonials data={landingPageData.Testimonials} isStandalone={isStandalone("/testimonials/")} /></section>
      <section id="page-team" data-route="/team/"><Team data={landingPageData.Team} isStandalone={isStandalone("/team/")} /></section>
      <section id="page-contact" data-route="/contact/"><Contact data={landingPageData.Contact} isStandalone={isStandalone("/contact/")} /></section>
      <section id="page-privacy-policy" data-route="/privacy-policy/"><Privpol data={landingPageData.Privpol} isStandalone={isStandalone("/privacy-policy/")} /></section>
    </>
  );

  return (
    <BrowserRouter basename="/">
      <RouteScrollSync />
      <div>
        <Navigation />
        <Routes>
          <Route path="*" element={scrollablePages} />
        </Routes>
      </div>
    </BrowserRouter>
  );
};

export default App;
